import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Lightning, ArrowRight, Package, CurrencyEur, Truck } from "@phosphor-icons/react";
import axios from "axios";
import { getRoleConfig } from "@/lib/roleNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * RoleWelcomeCard — small "today" snapshot shown on the landing page after
 * login. Each role gets metrics that matter to them. Hidden for admins
 * (they get their own dashboard).
 */
export default function RoleWelcomeCard({ user, setCurrentView }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);

  const role = user?.role;
  const cfg = getRoleConfig(role);

  useEffect(() => {
    if (!role || role === "admin" || role === "super_admin") return;
    const token = localStorage.getItem("blumove_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const fetchStats = async () => {
      try {
        if (role === "user") {
          const [me, mine] = await Promise.all([
            axios.get(`${API}/gamification/me`, { headers }).catch(() => ({ data: null })),
            axios.get(`${API}/my-shipments`, { headers }).catch(() => ({ data: [] })),
          ]);
          const list = Array.isArray(mine.data) ? mine.data : [];
          setStats({
            in_transit: list.filter((s) => s.status === "in_transit").length,
            delivered: list.filter((s) => s.status === "delivered").length,
            co2: me.data?.stats?.co2_saved ?? 0,
            total: list.length,
          });
        } else if (role === "driver") {
          const [veh, ships] = await Promise.all([
            axios.get(`${API}/vehicles/`).catch(() => ({ data: [] })),
            axios.get(`${API}/shipments`).catch(() => ({ data: [] })),
          ]);
          const vehs = Array.isArray(veh.data) ? veh.data : [];
          const shps = Array.isArray(ships.data) ? ships.data : [];
          setStats({
            assigned_vehicles: vehs.filter((v) => v.status === "active").length,
            in_transit: shps.filter((s) => s.status === "in_transit").length,
            today_delivered: shps.filter((s) => s.status === "delivered").length,
          });
        } else if (role === "intermediary") {
          const [pts, ships, fin] = await Promise.all([
            axios.get(`${API}/points`).catch(() => ({ data: [] })),
            axios.get(`${API}/shipments`).catch(() => ({ data: [] })),
            axios.get(`${API}/intermediaries/me`, { headers }).catch(() => ({ data: null })),
          ]);
          const shps = Array.isArray(ships.data) ? ships.data : [];
          setStats({
            today_revenue: fin.data?.today_revenue ?? null,
            arriving: shps.filter((s) => s.status === "in_transit").length,
            ready_for_pickup: shps.filter((s) => s.status === "at_delivery_point").length,
          });
        }
      } catch (e) { console.warn("RoleWelcomeCard: stats fetch failed", e); }
    };
    fetchStats();
  }, [role]);

  // Hide for admins (they have their own dashboard) and unauth users
  if (!cfg || !user || role === "admin" || role === "super_admin") return null;

  const RoleIcon = cfg.icon;
  const greeting = t("welcome.greeting", { name: (user.name || "").split(" ")[0] || user.email.split("@")[0] });

  const metrics = role === "user" ? [
    { label: t("welcome.user.in_transit"), value: stats?.in_transit ?? "–", icon: Lightning, hint: t("welcome.user.in_transit_hint") },
    { label: t("welcome.user.delivered"),  value: stats?.delivered  ?? "–", icon: Package },
    { label: t("welcome.user.co2"),         value: stats ? `${stats.co2.toFixed?.(1) ?? stats.co2} kg` : "–", icon: ArrowRight },
  ] : role === "driver" ? [
    { label: t("welcome.driver.routes_today"), value: stats?.assigned_vehicles ?? "–", icon: Truck },
    { label: t("welcome.driver.in_transit"),   value: stats?.in_transit ?? "–", icon: Lightning },
    { label: t("welcome.driver.delivered"),    value: stats?.today_delivered ?? "–", icon: Package },
  ] : [
    { label: t("welcome.intermediary.revenue"), value: stats?.today_revenue != null ? `${stats.today_revenue.toFixed(2)} €` : "–", icon: CurrencyEur },
    { label: t("welcome.intermediary.arriving"), value: stats?.arriving ?? "–", icon: Lightning },
    { label: t("welcome.intermediary.ready"),    value: stats?.ready_for_pickup ?? "–", icon: Package },
  ];

  return (
    <section data-testid={`role-welcome-${role}`} className="px-6 mt-4 mb-8" style={{ marginTop: "92px" }}>
      <div className="max-w-7xl mx-auto">
        <div
          className="bento-item"
          style={{
            padding: 28,
            borderRadius: 20,
            background: `linear-gradient(135deg, ${cfg.color}10 0%, ${cfg.color}03 60%, transparent 100%)`,
            borderTop: `3px solid ${cfg.color}`,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div style={{ position: "absolute", right: -20, top: -20, opacity: 0.06, fontSize: 220, lineHeight: 1, color: cfg.color }}>
            <RoleIcon weight="duotone" />
          </div>

          <div className="flex flex-wrap items-end justify-between gap-4" style={{ position: "relative", zIndex: 1 }}>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div style={{ width: 36, height: 36, borderRadius: 10, background: `${cfg.color}1f`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <RoleIcon size={20} weight="duotone" style={{ color: cfg.color }} />
                </div>
                <span style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: cfg.color }}>
                  {t(cfg.labelKey)}
                </span>
              </div>
              <h2 style={{ fontFamily: "Outfit", fontWeight: 800, fontSize: "1.7rem", marginBottom: 4, color: "var(--text-main)" }}>
                {greeting}
              </h2>
              <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", maxWidth: 480 }}>
                {t(`welcome.${role}.subtitle`)}
              </p>
            </div>

            <button
              data-testid={`role-welcome-cta-${role}`}
              onClick={() => setCurrentView(cfg.hero.view)}
              style={{
                background: cfg.color, color: "white",
                padding: "10px 20px", borderRadius: 9999,
                border: "none", fontFamily: "Outfit", fontWeight: 600,
                fontSize: "0.85rem", cursor: "pointer",
                display: "flex", alignItems: "center", gap: 6,
                transition: "filter 0.2s, transform 0.2s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.filter = "brightness(1.1)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.filter = "none"; e.currentTarget.style.transform = "none"; }}
            >
              {t(cfg.hero.labelKey)} <ArrowRight size={14} weight="bold" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6" style={{ position: "relative", zIndex: 1 }}>
            {metrics.map((m, i) => {
              const Icon = m.icon;
              return (
                <div key={i} data-testid={`role-welcome-metric-${i}`} style={{ padding: 16, borderRadius: 14, background: "var(--surface)", border: "1px solid var(--border-color)" }}>
                  <div className="flex items-center gap-2" style={{ color: cfg.color }}>
                    <Icon size={14} weight="duotone" />
                    <span style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)" }}>
                      {m.label}
                    </span>
                  </div>
                  <div style={{ fontFamily: "Outfit", fontWeight: 800, fontSize: "1.6rem", marginTop: 4, color: "var(--text-main)" }}>
                    {m.value}
                  </div>
                  {m.hint && (
                    <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>{m.hint}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
